import { Upload as UploadIcon } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import type React from "react";
import { useState } from "react";
import { VisuallyHiddenInput } from "@/components/ui";
import { useApi, useApiMutation, useApiQuery, type WithDynamicUrl } from "../../hooks";
import type { DataAsset } from "../../types";
import { DataAssetsTable } from "../data-assets-table";

const ALLOWED_EXTENSIONS = [
  // Documents
  ".csv",
  ".json",
  ".xlsx",
  ".xls",
  ".txt",
  ".pdf",
  ".md",
  // Images
  ".jpg",
  ".jpeg",
  ".png",
  ".gif",
  ".webp",
  ".bmp",
  ".svg",
  ".ico",
  ".tiff",
  ".tif",
  // Videos
  ".mp4",
  ".mov",
  ".avi",
  ".webm",
  ".mkv",
  ".flv",
  ".wmv",
  ".m4v",
];

export const DataAssetTab: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { callApi } = useApi<DataAsset>();
  const [isUploading, setIsUploading] = useState(false);

  const { data: dataAssets, isLoading, refetch } = useApiQuery<DataAsset[]>("/data-assets");

  const { mutate: updateAsset } = useApiMutation<WithDynamicUrl<Partial<DataAsset>>, DataAsset>(
    "PATCH",
    "",
    {
      onSuccess: () => {
        enqueueSnackbar("Data asset updated successfully", {
          variant: "success",
        });
        refetch();
      },
      onError: () => {
        enqueueSnackbar("Failed to update data asset", { variant: "error" });
      },
    },
  );

  const { mutate: deleteAsset } = useApiMutation<void, void>("DELETE", "/data-assets", {
    onSuccess: () => {
      enqueueSnackbar("Data asset deleted successfully", {
        variant: "success",
      });
      refetch();
    },
    onError: () => {
      enqueueSnackbar("Failed to delete data asset", { variant: "error" });
    },
  });

  const { mutate: uploadFile } = useMutation({
    mutationFn: async (file: File) => {
      setIsUploading(true);
      const formData = new FormData();

      formData.append("file", file);
      formData.append("filename", file.name);

      return callApi("/data-assets-upload", {
        method: "POST",
        body: formData,
      });
    },
    onSuccess: () => {
      enqueueSnackbar("Data asset uploaded successfully", {
        variant: "success",
      });
      refetch();
      setIsUploading(false);
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (error: any) => {
      const message = error?.message || "Failed to upload data asset";
      enqueueSnackbar(message, { variant: "error" });
      setIsUploading(false);
    },
  });

  const { mutate: fetchAssetById } = useMutation({
    mutationFn: async (id: string) => {
      return callApi(`/data-assets/${id}`, {
        method: "GET",
      });
    },
    onSuccess: (data: DataAsset) => {
      if (data.file_path) {
        window.open(data.file_path, "_blank");
      } else if (data.raw_data) {
        const blob = new Blob([data.raw_data], { type: "text/plain" });
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      } else {
        enqueueSnackbar("No content available for this asset", {
          variant: "warning",
        });
      }
    },
    onError: () => {
      enqueueSnackbar("Failed to fetch data asset", { variant: "error" });
    },
  });

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const fileExtension = `.${file.name.split(".").pop()?.toLowerCase()}`;
    if (!ALLOWED_EXTENSIONS.includes(fileExtension)) {
      enqueueSnackbar(`Invalid file type. Allowed extensions: ${ALLOWED_EXTENSIONS.join(", ")}`, {
        variant: "error",
      });
      return;
    }

    uploadFile(file);
  };

  const handleEdit = (asset: DataAsset, newName: string) => {
    updateAsset({
      _url: `/data-assets/${asset.id}`,
      name: newName,
    });
  };

  const handleDelete = (asset: DataAsset) => {
    deleteAsset({
      _url: `/data-assets/${asset.id}`,
    });
  };

  const handleView = (asset: DataAsset) => {
    fetchAssetById(asset.id);
  };

  return (
    <Card>
      <CardContent>
        <Stack spacing={3}>
          <Box>
            <Typography variant="h6" gutterBottom>
              Data Assets
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Manage data assets that can be used in your tests. Upload files containing test data
              such as CSV, JSON, Excel, or text files.
            </Typography>
          </Box>

          <Stack spacing={1} direction="row" alignItems="center" justifyContent="space-between">
            <Alert severity="info" sx={{ width: "100%", flex: 1 }}>
              Allowed file extensions: {ALLOWED_EXTENSIONS.join(", ")}
            </Alert>

            <Button
              component="label"
              variant="contained"
              size="small"
              startIcon={isUploading ? <CircularProgress size={20} /> : <UploadIcon />}
              disabled={isUploading}
              sx={{
                width: "fit-content",
              }}
            >
              {isUploading ? "Uploading..." : "Upload Data Asset"}
              <VisuallyHiddenInput
                type="file"
                onChange={handleFileSelect}
                accept={ALLOWED_EXTENSIONS.join(",")}
                disabled={isUploading}
              />
            </Button>
          </Stack>

          <DataAssetsTable
            data={dataAssets || []}
            isLoading={isLoading}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onView={handleView}
            height={400}
          />
        </Stack>
      </CardContent>
    </Card>
  );
};
